import React from "react";
import { Link } from "react-router-dom";
import class1 from "../Assets/class1.jpg";
import class2 from "../Assets/class2.jpg";
import { motion } from "framer-motion";
import {
  GraduationCap,
  BookOpen,
  Dumbbell,
  Users,
  Landmark,
  School,
  Hospital,
  ScaleIcon,
} from "lucide-react";
import ImageCarousel from "../components/ImageCarousel";
import PromoSection from "../components/PromoSection";
import { Helmet } from "react-helmet-async";

export const Home: React.FC = () => (
  <main className="w-full bg-gradient-to-b from-yellow-100 via-white to-blue-100 text-gray-800 overflow-x-hidden">
    {/* Page-level SEO */}
    <Helmet>
      {/* SEO Title */}
      <title>
        Haifa Defence Academy Agra | Sainik & Military School Coaching for Class
        6 & 9
      </title>

      {/* SEO Description */}
      <meta
        name="description"
        content="Haifa Defence Academy in Agra provides specialised Sainik School, Rashtriya Military School (RMS), RIMC and defence school entrance coaching for Class 6 & 9 with schooling, hostel and full exam preparation."
      />

      {/* SEO Keywords */}
      <meta
        name="keywords"
        content="Haifa defence academy Agra, sainik school coaching in Agra, sainik school entrance coaching class 6, sainik school entrance coaching class 9, sainik school preparation for class 6, sainik school preparation for class 9, sainik school coaching near me, sainik school academy near me, military school coaching in Agra, military school coaching near me, military school entrance coaching, RMS coaching in Agra, RMS coaching for class 6, rashtriya military school coaching Agra, defence academy in Agra, defence coaching institute in Agra, best sainik school coaching in Agra, best military school coaching in Agra, coaching for sainik school exam, defence school preparation coaching, defence coaching for school students"
      />
    </Helmet>

    <div className="relative h-80 w-full mb-16">
      <ImageCarousel autoPlay={true} interval={4000} />
      <div className="absolute inset-0 flex flex-col justify-center items-center text-white text-center z-10">
        <motion.div
          className="relative z-10 text-center text-white px-4 max-w-4xl"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          <h1 className="text-4xl md:text-5xl font-extrabold drop-shadow mb-4">
            Haifa Defence Academy
          </h1>
          <p className="text-lg md:text-xl font-light mb-6">
            Empowering Youth for Nation’s Defence Forces
          </p>
          <Link
            to="/contact"
            className="inline-block bg-yellow-400 hover:bg-yellow-500 text-blue-900 font-bold px-8 py-3 rounded-full shadow-md transition"
          >
            Contact Us
          </Link>
        </motion.div>
      </div>
    </div>
    <PromoSection />

    {/* Curriculum Overview */}
    <motion.section
      className="py-16 bg-gradient-to-b from-white to-yellow-50"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <div className="max-w-6xl mx-auto px-4 text-center">
        <div className="inline-flex items-center justify-center gap-3 bg-yellow-100 border-l-4 border-yellow-400 px-4 py-2 rounded shadow-sm mb-4">
          <h2 className="text-3xl font-bold text-blue-900">
            Explore Curriculum & Facilities
          </h2>
        </div>
        <p className="text-gray-700 max-w-3xl mx-auto mb-6 text-lg">
          Haifa Defence Academy offers a focused curriculum for Sainik and
          Military School aspirants, covering academics, aptitude, personality
          development, and physical training — all supported by modern
          classrooms, AC hostels, sports, and labs.
        </p>
        <Link
          to="/courses"
          className="inline-block bg-yellow-400 hover:bg-yellow-500 text-blue-900 font-bold px-6 py-3 rounded-xl shadow transition-transform hover:scale-105"
        >
          View Courses & Infrastructure
        </Link>
      </div>
    </motion.section>

    {/* Key Features Section */}
    <motion.section
      className="bg-gradient-to-r from-yellow-50 via-white to-yellow-50 py-14"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 1 }}
    >
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center gap-3 bg-yellow-100 border-l-4 border-yellow-400 px-4 py-2 rounded shadow-sm">
            <h2 className="text-3xl font-bold text-blue-900">Key Features</h2>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              icon: School, // make sure imported from lucide-react or react-icons
              title: "Schooling",
              desc: "Regular schooling support aligned with Sainik & Military School standards.",
            },
            {
              icon: GraduationCap,
              title: "Entrance Preparation",
              desc: "Focused coaching for Sainik & Military School entrance exams.",
            },
            {
              icon: BookOpen,
              title: "Holistic Curriculum",
              desc: "Covers academics, reasoning, general knowledge & personality development.",
            },
            {
              icon: Dumbbell,
              title: "Physical & Mental Fitness",
              desc: "Structured fitness programs, leadership, and team-building.",
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="bg-white border rounded-xl p-6 text-center shadow hover:shadow-lg transition"
              whileHover={{ scale: 1.05 }}
            >
              <item.icon className="h-10 w-10 text-yellow-400 mx-auto mb-2" />
              <h3 className="text-lg font-semibold mb-2 text-blue-900">
                {item.title}
              </h3>
              <p className="text-gray-600">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>

    {/* Admission Pathways */}
    <section className="bg-gradient-to-b from-white via-blue-50 to-yellow-50 py-20">
      <div className="max-w-6xl mx-auto px-4 text-center">
        <div className="inline-flex items-center justify-center gap-3 bg-yellow-100 border-l-4 border-yellow-400 px-4 py-2 rounded shadow-sm mb-6">
          <h2 className="text-3xl font-bold text-blue-900">
            Admission Pathways
          </h2>
        </div>
        <p className="text-gray-700 max-w-3xl mx-auto mb-10 text-lg">
          Our programs are specially curated for students of Classes 6 and 9,
          preparing them for Sainik School, Military School, and RMS entrance
          exams.
        </p>

        <div className="grid md:grid-cols-2 gap-8">
          {[
            {
              icon: Users,
              title: "Class 3 to 5",
              desc: "Foundation batch for early starters targeting RMS and Sainik School exams in later years.",
              features: [
                "Basic Academic Strengthening",
                "Intro to Reasoning & GK",
                "Morning Yoga + Discipline Routines",
                "Smart Classes, Library, Hostel, and Sports",
              ],
              image: class1,
            },
            {
              icon: ScaleIcon,
              title: "Class 6 to 8",
              desc: "Focused entrance training for Military School, Sainik School & RMS with full academic support.",
              features: [
                "Entrance Pattern-Based Teaching",
                "Mock Tests, Interviews, Personality Grooming",
                "Morning Yoga + Discipline Routines",
                "Smart Classes, Library, Hostel, and Sports",
              ],
              image: class2,
            },
          ].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition transform hover:-translate-y-1"
            >
              <img
                src={item.image}
                alt={item.title}
                className="w-full h-52 object-cover"
              />
              <div className="p-6 text-left">
                <item.icon className="h-8 w-8 text-blue-900 mb-2" />
                <h3 className="text-xl font-bold text-blue-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-700 mb-3">{item.desc}</p>
                <ul className="list-disc list-inside text-sm text-gray-600 space-y-1 mb-3">
                  {item.features.map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
                <Link
                  to="/courses"
                  className="inline-block bg-yellow-400 hover:bg-yellow-500 text-blue-900 font-semibold px-4 py-2 rounded transition"
                >
                  Learn More
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* Facilities */}
    <motion.section
      className="bg-blue-50 py-16"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <div className="max-w-6xl mx-auto px-4 text-center">
        <div className="inline-flex items-center justify-center gap-3 bg-yellow-100 border-l-4 border-yellow-400 px-4 py-2 rounded shadow-sm mb-6">
          <h2 className="text-2xl font-bold text-blue-900">
            World-Class Facilities
          </h2>
        </div>
        <p className="text-gray-700 mb-4">
          Modern tech-enabled classrooms, labs, hostels & sports infrastructure.
        </p>
        <div className="grid md:grid-cols-3 gap-6 mt-8">
          {[
            { icon: School, label: "Smart Classrooms & Computer Lab" },
            { icon: Landmark, label: "Dedicated Sports Complex" },
            {
              icon: Hospital,
              label: "Comfortable AC Hostel & Medical Support",
            },
          ].map((item, i) => (
            <motion.div
              key={i}
              className="bg-white rounded-xl p-6 shadow hover:shadow-lg transition"
              whileHover={{ scale: 1.05 }}
            >
              <item.icon className="h-8 w-8 text-blue-900 mb-3 mx-auto" />
              <h3 className="text-blue-900 font-semibold text-lg">
                {item.label}
              </h3>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>

    {/* CTA */}
    <motion.section
      className="bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-400 py-12 text-center"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <h3 className="text-xl font-bold text-blue-900">Get in Touch Today</h3>
      <p className="mb-4 text-blue-900">
        Begin your journey to a disciplined and purposeful future.
      </p>
      <Link
        to="/contact"
        className="inline-block bg-blue-900 hover:bg-blue-800 text-white font-semibold px-6 py-3 rounded-full shadow transition"
      >
        Contact Us
      </Link>
    </motion.section>
    {/* WhatsApp CTA */}
    <a
      href="https://wa.me/919997450792?text=Hi%20I%20am%20interested%20in%20your%20academy%20program"
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 right-6 z-50 flex items-center gap-3 bg-green-500 hover:bg-green-600 text-white px-5 py-3 rounded-full transition-all duration-300 animate-pulse font-semibold shadow-lg"
      title="Chat on WhatsApp"
      style={{
        boxShadow: `
            0 0 15px rgba(0, 255, 0, 0.6),
            0 0 25px rgba(0, 255, 0, 0.5),
            0 4px 10px rgba(0, 0, 0, 0.3)
          `,
      }}
    >
      <img
        src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
        alt="WhatsApp"
        className="h-8 w-8"
      />
      <span className="hidden sm:inline">Chat</span>
    </a>
  </main>
);
